import { ReactElement, Suspense, useMemo } from 'react';
import { Avatar } from '@cbhq/cds-web/media/Avatar';
import { Pressable } from '@cbhq/cds-web/system';
import { FetchableResourceProvider } from '@cbhq/instant-api-hooks-core/fetch';

import { HelpMenu } from '../HelpMenu';
import { OrgSwitcherContainer } from '../Organization/OrgSwitcherContainer';
import { ResourceStorage } from '../Organization/ResourceStorage';

import { ErrorBoundary } from './components/ErrorBoundary';
import { ManagedByExplanationTray } from './components/ManagedByExplanationTray';
import { PasskeyGuideTourModal } from './components/PasskeyGuideTourModal';
import { ProfileDropdown } from './components/ProfileDropdown';
import { ProfileDropdownFallback } from './components/ProfileDropdownFallback';
import { useFetchWithMiddleware } from './hooks/useFetchWithMiddleware';
import { useClientProps } from './providers/ClientPropsProvider';
import { ManagedByExplanationProvider } from './providers/ManagedByExplanationProvider';
import { ProfileMenuV2Provider } from './providers/ProfileMenuV2Provider';

type ConditionalBoundaryWrapperProps = { children: ReactElement };

function ConditionalBoundaryWrapper({ children }: ConditionalBoundaryWrapperProps) {
  /**
   * If boundary fallback is enabled, we don't want to wrap the children in an inner error boundary.
   */
  const { boundaryFallback } = useClientProps();

  const fallback = useMemo(
    () => (
      <Pressable background="transparent">
        <Avatar alt="User Avatar" size="xl" colorScheme="teal" />
      </Pressable>
    ),
    [],
  );

  if (boundaryFallback) {
    return children;
  }

  return <ErrorBoundary fallback={fallback}>{children}</ErrorBoundary>;
}

export function ProfileMenu() {
  const fetchFunction = useFetchWithMiddleware();

  const { config } = useClientProps();

  ResourceStorage.setMUSEnabled(config?.orgSwitcherEnabled ?? false);
  const orgSwitcherEnabled = ResourceStorage.MUSEnabled() && config?.orgSwitcherEnabled;

  return (
    <FetchableResourceProvider fetch={fetchFunction}>
      <ConditionalBoundaryWrapper>
        <Suspense fallback={<ProfileDropdownFallback />}>
          <ProfileMenuV2Provider>
            <ManagedByExplanationProvider>
              <HelpMenu />
              {orgSwitcherEnabled && <OrgSwitcherContainer />}
              <ProfileDropdown />
              <PasskeyGuideTourModal />
              <ManagedByExplanationTray />
            </ManagedByExplanationProvider>
          </ProfileMenuV2Provider>
        </Suspense>
      </ConditionalBoundaryWrapper>
    </FetchableResourceProvider>
  );
}
